<script>
  import { navigate } from '../../lib/stores/router.svelte.js';
  import { isUserAnonymous, initiateDiscordOAuth } from '../../lib/stores/auth.svelte.js';
  import { groupsState, refreshUserGroups } from '../../lib/stores/groups.svelte.js';
  import { browseState } from '../../lib/stores/browse.svelte.js';
  import { buildMemberAvatarBadges, subgroupSummary } from '../../lib/logic/groups-logic.js';
  import CreateGroupModal from './CreateGroupModal.svelte';
  import Avatar from '../shared/Avatar.svelte';

  let showCreateModal = $state(false);

  function getRoleBadgeClass(role) {
    if (role === 'owner') return 'role-owner';
    if (role === 'admin') return 'role-admin';
    return 'role-member';
  }

  function getRoleLabel(role) {
    if (role === 'owner') return 'Owner';
    if (role === 'admin') return 'Admin';
    return 'Member';
  }

  async function handleGroupCreated() {
    showCreateModal = false;
    await refreshUserGroups();
  }

  function handleGroupClick(group) {
    browseState.selectedGroupId = group.groupId;
    navigate('search');
  }

  function getMemberCount(group) {
    const uids = groupsState.memberUidsByGroup[group.groupId];
    return uids ? uids.length : 0;
  }

  function getMemberAvatars(group) {
    const uids = groupsState.memberUidsByGroup[group.groupId] || [];
    return buildMemberAvatarBadges(uids, groupsState.memberUidToName, groupsState.memberUidToPhoto);
  }
</script>

<main class="groups-page">
  <div class="groups-container">
    <div class="groups-header">
      <h2 class="groups-title">Your Groups</h2>
      <p class="groups-summary">{groupsState.userGroups.length} groups</p>
    </div>

    {#if isUserAnonymous()}
      <div class="groups-auth-gate">
        <div class="auth-gate-icon">👥</div>
        <h2>Sign in to use Groups</h2>
        <p>
          Groups let you see which cards your trading partners are looking for, highlighted right in
          their binders.
        </p>
        <button class="btn-discord" onclick={() => initiateDiscordOAuth('/groups')}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path
              d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"
            />
          </svg>
          Sign in with Discord
        </button>
      </div>
    {:else if groupsState.loading}
      <div class="groups-loading">Loading groups...</div>
    {:else}
      <div class="groups-list">
        {#each groupsState.userGroups as group, i (group.groupId)}
          {@const memberCount = getMemberCount(group)}
          {@const avatars = getMemberAvatars(group)}
          <button
            class="group-card"
            style="animation-delay: {i * 0.08}s"
            onclick={() => handleGroupClick(group)}
          >
            <div class="group-card-top">
              <div class="group-card-info">
                <div class="group-name">{group.name}</div>
                <div class="group-meta">
                  {memberCount} member{memberCount !== 1 ? 's' : ''}
                  <span class="role-badge {getRoleBadgeClass(group.role)}"
                    >{getRoleLabel(group.role)}</span
                  >
                </div>
                {#if subgroupSummary(group)}
                  <div class="group-subgroups">{subgroupSummary(group)}</div>
                {/if}
              </div>
              <span class="group-chevron">›</span>
            </div>

            {#if avatars.length > 0}
              <div class="group-avatars">
                {#each avatars as avatar (avatar.uid)}
                  <Avatar
                    id={avatar.uid}
                    name={avatar.name}
                    photoUrl={avatar.photoUrl}
                    alt={avatar.name}
                    title={avatar.name}
                  />
                {/each}
              </div>
            {/if}
          </button>
        {/each}

        <!-- Join or Create placeholder -->
        <button class="group-card-create" onclick={() => (showCreateModal = true)}>
          <div class="create-icon">+</div>
          <div class="create-label">Join or Create Group</div>
        </button>
      </div>
    {/if}
  </div>
</main>

{#if showCreateModal}
  <CreateGroupModal onClose={() => (showCreateModal = false)} onCreated={handleGroupCreated} />
{/if}

<style>
  .groups-page {
    padding: var(--space-lg) var(--space-xl) 80px;
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
  }

  .groups-container {
    width: 100%;
  }

  .groups-header {
    margin-bottom: var(--space-lg);
  }

  .groups-title {
    font-family: var(--font-serif);
    font-size: 1.625rem;
    font-weight: 700;
    color: var(--color-text);
    margin: 0 0 var(--space-xs);
  }

  .groups-summary {
    font-size: 13px;
    color: var(--color-text-tertiary);
    font-family: var(--font-mono);
    margin: 0;
  }

  .groups-loading {
    text-align: center;
    color: var(--color-text-tertiary);
    padding: 48px 0;
  }

  .groups-auth-gate {
    text-align: center;
    padding: 64px 24px;
    background: var(--color-bg-surface);
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border);
  }

  .auth-gate-icon {
    font-size: 3rem;
    margin-bottom: 16px;
  }

  .groups-auth-gate h2 {
    font-family: var(--font-serif);
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-text);
    margin: 0 0 8px;
  }

  .groups-auth-gate p {
    font-size: 14px;
    color: var(--color-text-tertiary);
    margin: 0 0 16px;
    line-height: 1.5;
  }

  .btn-discord {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: #5865f2;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
    font-family: inherit;
  }

  .btn-discord:hover {
    background: #4752c4;
  }

  .groups-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .group-card {
    all: unset;
    cursor: pointer;
    background: var(--color-bg-surface);
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border);
    padding: 20px 24px;
    animation: fadeUp 0.3s ease both;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;
  }

  .group-card:hover {
    border-color: var(--color-text-muted);
    box-shadow: 0 2px 12px rgba(40, 30, 15, 0.06);
  }

  @keyframes fadeUp {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .group-card-top {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
  }

  .group-card-info {
    flex: 1;
  }

  .group-name {
    font-family: var(--font-serif);
    font-size: 19px;
    font-weight: 700;
    color: var(--color-text);
  }

  .group-meta {
    font-size: 13px;
    color: var(--color-text-tertiary);
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .group-subgroups {
    font-size: 12px;
    color: var(--color-text-tertiary);
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .group-chevron {
    font-size: 20px;
    color: var(--color-text-placeholder);
    flex-shrink: 0;
  }

  .group-avatars {
    display: flex;
    gap: 8px;
    --avatar-size: 32px;
    --avatar-font-size: 13px;
  }

  .role-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .role-owner {
    background: #fef3c7;
    color: #92400e;
  }

  .role-admin {
    background: var(--color-accent-bg);
    color: var(--color-text-secondary);
  }

  .role-member {
    background: var(--color-bg-tray);
    color: var(--color-text-tertiary);
  }

  .group-card-create {
    all: unset;
    cursor: pointer;
    border-radius: var(--radius-lg);
    border: 2px dashed var(--color-border-subtle);
    padding: 32px 24px;
    text-align: center;
  }

  .group-card-create:hover {
    border-color: var(--color-text-muted);
  }

  .create-icon {
    font-size: 22px;
    margin-bottom: 6px;
    opacity: 0.3;
    color: var(--color-text);
  }

  .create-label {
    font-family: var(--font-serif);
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-muted);
  }
</style>
